// HomeActivityBurst.jsx

import React, { useMemo } from 'react';

// Utilities
import { groupActivityItemsByDay } from 'node/activity/utils/groupActivityItemsByDay';
import {
  HOME_ACTIVITY_PHOTO_GROUP_KIND,
  groupNearbyPhotoItems,
} from 'home/activity/utils/groupNearbyPhotoItems';

// CSS
import './HomeActivityBurst.css';

// Components
import HomeActivityItem from 'home/activity/HomeActivityItem';
import HomeActivityPhotoGroup from 'home/activity/HomeActivityPhotoGroup';

/**
 * HomeActivityBurst
 *
 * One consecutive same-project run as a titled card, with items
 * broken into a day timeline.
 *
 * @param {object} props
 * @param {object} props.burst - Grouped burst from groupActivityBursts.
 * @param {function} [props.onOpenNode] - Opens a node via type policy (Peek or Workspace).
 * @returns {ReactNode}
 */
const HomeActivityBurst = React.memo(({ burst, onOpenNode }) => {
  const items = burst?.items;
  const projectLabel = String(burst?.projectLabel || '').trim();
  const projectNode = burst?.projectNode || null;

  // Day sections, then nearby photo adds collapsed into a thumb grid.
  const daySections = useMemo(
    () => groupActivityItemsByDay(items).map((section) => ({
      ...section,
      items: groupNearbyPhotoItems(section.items),
    })),
    [items]
  );

  /**
   * handleOpenProject
   *
   * Opens the burst's project node.
   *
   * @returns {void}
   */
  const handleOpenProject = () => {
    if (typeof onOpenNode === 'function' && projectNode) {
      onOpenNode(projectNode);
    }
  };

  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <article className="home-activity-burst-container">

      {/* TITLE */}
      {projectLabel ? (
        <button
          className="home-activity-burst-title"
          type="button"
          onClick={handleOpenProject}
        >
          {projectLabel}
        </button>
      ) : null}

      {daySections.map((section) => (
        <div
          key={section.key}
          className="home-activity-burst-day"
        >

          {/* DAY */}
          {section.dateLabel ? (
            <div className="home-activity-burst-day-label">
              {section.dateLabel}
            </div>
          ) : null}

          {/* ROWS */}
          <div className="home-activity-burst-rows">
            {section.items.map((item) => (
              item.kind === HOME_ACTIVITY_PHOTO_GROUP_KIND ? (
                <HomeActivityPhotoGroup
                  key={item.key}
                  item={item}
                  timestampStyle={section.dateLabel ? 'time' : 'datetime'}
                />
              ) : (
                <HomeActivityItem
                  key={item.key}
                  item={item}
                  timestampStyle={section.dateLabel ? 'time' : 'datetime'}
                  onOpenNode={onOpenNode}
                />
              )
            ))}
          </div>
        </div>
      ))}
    </article>
  );
});

HomeActivityBurst.displayName = 'HomeActivityBurst';

export default HomeActivityBurst;
